import { StyleSheet, css } from 'aphrodite';
import * as React from 'react';
import Markdown from 'react-markdown';
import { useLocation, useParams } from 'react-router';
import { isWebsiteContentMarkdownContent, nestWebsiteContentList } from '../../../admin/containers/CopyMageWireframe';
import { MarkdownContent } from '../../../extViewmodels/sdk/models/Website';
import { LoadingSpinner } from '../../../web/components/LoadingSpinner';
import { StickyNoteIcon } from '../../../web/components/svgs/icons/StickyNoteIcon';
import { background, brandPrimary, charmGray, shuffleGray, titles, white } from '../../../web/styles/colors';
import { bs } from '../../../web/styles/styles';
import { useWebsiteContentListViewAppQuery } from '../../queries/useWebsiteContentListViewAppQuery';
import { useWebsiteViewAppQuery } from '../../queries/useWebsiteViewAppQuery';

const s = StyleSheet.create({
	root: {
		display: 'flex',
		background: white,
		height: `100%`,
		flexDirection: `column`,
	},
	sidebar: {
		display: 'flex',
		flexDirection: 'column',
		backgroundColor: background,
		height: '100%',
		borderRight: `1px solid ${shuffleGray}`,
		padding: `32px 8px`,
		width: 228,
		boxSizing: 'border-box',
	},
	content: {
		display: 'flex',
		flexDirection: 'column',
		padding: `16px 16px 16px 54px`,
		maxWidth: 686,
	},
	sidebarTitle: {
		color: brandPrimary,
		fontSize: `1rem`,
		marginBottom: 16,
	},
	sidebarMenuItem: {
		display: `flex`,
		flexDirection: `column`,
		gap: 8,
	},
	sidebarMenuItemInner: {
		boxSizing: `border-box`,
		color: titles,
		backgroundColor: '#9198A407',
		padding: 8,
		display: 'flex',
		alignItems: 'center',
		gap: 8,
		borderRadius: 16,
		maxWidth: 208,
		':hover': {
			backgroundColor: '#9198A41A',
		},
	},
	sidebarNestedMenuItemButton: {
		color: charmGray,
	},
	sidebarContainer: {
		display: `flex`,
		flexDirection: `column`,
		gap: 8,
	},
	sidebarItemContainer: {
		position: `relative`,
	},
	sidebarNestedContainer: {
		paddingLeft: 16,
	},
	sidebarMenuItemSelected: {
		color: titles,
		backgroundColor: '#0466FF07',
	},
	sidebarMenuItemIcon: {
		color: charmGray,
		width: 12,
		height: 12,
	},
	sidebarMenuItemIconSelected: {
		color: brandPrimary,
	},
	sidebarMenuItemButton: {
		flex: 1,
	},
	contentMd: {
		backgroundColor: background,
		borderRadius: 8,
		border: `1px solid ${shuffleGray}`,
		padding: `16px 24px`,
		marginBottom: 24,
		flex: 1,
		overflow: 'auto',
		fontSize: '0.875rem',
		lineHeight: `30px`,
		':has(*) ul,ol': {
			paddingLeft: 16,
			margin: 0,
			listStyle: 'revert',
		},
		':has(*) li': {
			listStyle: 'inherit',
		},
	},
	loadingContainer: {
		display: `flex`,
		justifyContent: `center`,
		padding: 16,
	},
	appBar: {
		backgroundColor: brandPrimary,
		color: white,
		display: `flex`,
		justifyContent: `center`,
		alignItems: `center`,
		minHeight: 56,
		fontSize: `1.25rem`,
	},
	main: {
		display: `flex`,
		flex: 1,
		maxHeight: `calc(100% - 56px)`,
	},
});

export function CopyMageWebsitePage() {
	const location = useLocation();
	const params = useParams<{ id: string }>();
	const tokenParam = new URLSearchParams(location.search).get(`token`);
	const [selectedContentId, setSelectedContentId] = React.useState<string | null>(null);
	const websiteContentListQuery = useWebsiteContentListViewAppQuery({
		enabled: Boolean(params.id) && Boolean(tokenParam),
		id: params.id,
		token: tokenParam,
	});

	if (!websiteContentListQuery.data) {
		return (
			<CopyMageWebsitePageLayout>
				<div className={css(s.loadingContainer)}>
					<LoadingSpinner />
				</div>
			</CopyMageWebsitePageLayout>
		);
	}
	const markdownContentList = websiteContentListQuery.data?.filter(isWebsiteContentMarkdownContent) ?? [];
	const selectedContent = selectedContentId ? markdownContentList.find(c => c.id === selectedContentId) : null;
	const nestedContentList = nestWebsiteContentList<MarkdownContent>(markdownContentList);

	return (
		<CopyMageWebsitePageLayout>
			<div className={css(s.main)}>
				<div className={css(s.sidebar)}>
					<div className={css(s.sidebarTitle)}>Content</div>
					<div className={css(s.sidebarContainer)}>
						{nestedContentList.map(entry => {
							const content = entry.parent;
							return (
								<div key={content.id} className={css(s.sidebarItemContainer)}>
									<div className={css(s.sidebarMenuItem)}>
										<div
											className={css(
												s.sidebarMenuItemInner,
												bs.truncateText,
												selectedContentId === content.id && s.sidebarMenuItemSelected
											)}
											onClick={() => setSelectedContentId(content.id)}
										>
											<StickyNoteIcon
												className={css(
													s.sidebarMenuItemIcon,
													selectedContentId === content.id && s.sidebarMenuItemIconSelected
												)}
											/>
											<button className={css(s.sidebarMenuItemButton)}>{content.name}</button>
										</div>
										{entry.children.map(childContent => {
											return (
												<div key={childContent.id} className={css(s.sidebarNestedContainer)}>
													<div
														className={css(
															s.sidebarMenuItemInner,
															bs.truncateText,
															selectedContentId === childContent.id && s.sidebarMenuItemSelected
														)}
														onClick={() => setSelectedContentId(childContent.id)}
													>
														<button className={css(s.sidebarMenuItemButton, s.sidebarNestedMenuItemButton)}>
															{childContent.name}
														</button>
													</div>
												</div>
											);
										})}
									</div>
								</div>
							);
						})}
					</div>
				</div>
				<div className={css(s.content)}>
					{selectedContent ? (
						<>
							<div className={css(s.contentMd)}>
								<Markdown>{selectedContent.body}</Markdown>
							</div>
						</>
					) : null}
				</div>
			</div>
		</CopyMageWebsitePageLayout>
	);
}

const CopyMageWebsitePageLayout = ({ children }: { children: React.ReactNode }) => {
	const location = useLocation();
	const params = useParams<{ id: string }>();
	const tokenParam = new URLSearchParams(location.search).get(`token`);
	const websiteViewappQuery = useWebsiteViewAppQuery({
		enabled: Boolean(params.id) && Boolean(tokenParam),
		id: params.id,
		token: tokenParam,
	});
	return (
		<div className={css(s.root)}>
			<header className={css(s.appBar)}>{websiteViewappQuery.data?.name}</header>
			{children}
		</div>
	);
};
